import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useLeadStore, useLeaseCompStore, usePipedriveStore, useSaleCompStore, useSearchStore, useTenantInMarketStore } from "../..";
import { RecentItemType } from "../../interfaces/IRecentItem";
import CompanyInfoWithIconAndNavigation from "../companies/CompanyInfoWithIconAndNavigation";
import DealInfoWithIconAndNavigation from "../deals/DealInfoWithIconAndNavigation";
import LeadInfoWithIconAndNavigation from "../leads/LeadInfoWithIconAndNavigation";
import LeaseCompInfoWithIconAndNavigation from "../leaseComps/LeaseCompInfoWithIconAndNavigation";
import SaleCompInfoWithIconAndNavigation from "../saleComps/SaleCompInfoWithIconAndNavigation";
import TenantInMarketInfoWithIconAndNavigation from "../tenantsInMarket/TenantInMarketInfoWithIconAndNavigation";

function RecentViewItem({ id, type, className, index }: IRecentViewItemProps) {
    const leadStore = useLeadStore();
    const leaseCompStore = useLeaseCompStore();
    const saleCompStore = useSaleCompStore();
    const searchStore = useSearchStore();
    const pipedriveStore = usePipedriveStore();
    const tenantInMarketStore = useTenantInMarketStore();
    const deal = useMemo(() => computed(() => type === RecentItemType.Deal ? pipedriveStore.deals.find(d => d.id === id) : null), [id, type]).get();
    const lead = useMemo(() => computed(() => type === RecentItemType.Lead ? leadStore.leads.find(l => l.id === id) : null), [id, type]).get();
    const leaseComp = useMemo(() => computed(() => type === RecentItemType.LeaseComp ? leaseCompStore.leaseComps.find(lc => lc.id === id) : null), [id, type]).get();
    const saleComp = useMemo(() => computed(() => type === RecentItemType.SaleComp ? saleCompStore.saleComps.find(sc => sc.id === id) : null), [id, type]).get();
    const tenantInMarket = useMemo(() => computed(() => type === RecentItemType.TenantInMarket ? tenantInMarketStore.tenantsInMarket.find(tim => tim.id === id) : null), [id, type]).get();
    const company = useMemo(() => computed(() => type === RecentItemType.Company ? pipedriveStore.organizations.find(o => o.id === id) : null), [id, type]).get();

    const onClick = () => searchStore.setIsOpen(false);

    if (!deal && !lead && !leaseComp && !saleComp && !tenantInMarket && !company)
        return null;

    return (
        <>
            {type === RecentItemType.Deal && (
                <DealInfoWithIconAndNavigation
                    id={deal!.id}
                    name={deal!.name}
                    value={deal!.value}
                    status={deal!.status}
                    stageId={deal!.dealStageId}
                    buildingSize={deal!.buildingSize}
                    landSizeAcres={deal!.landSizeAcres}
                    zoning={deal!.zoning}
                    className={className}
                    onClick={onClick}
                    index={index}
                />
            )}
            {type === RecentItemType.Lead && (
                <LeadInfoWithIconAndNavigation
                    id={lead!.id}
                    address={lead!.address}
                    ownerName={lead!.ownerName}
                    status={lead!.status}
                    buildingSize={lead!.buildingSize}
                    landSizeAcres={lead!.landSize}
                    zoning={lead!.zoning}
                    className={className}
                    onClick={onClick}
                    index={index}
                />
            )}
            {type === RecentItemType.LeaseComp && (
                <LeaseCompInfoWithIconAndNavigation
                    id={leaseComp!.id}
                    pipedriveCompanyId={leaseComp!.pipedriveCompanyId}
                    address={leaseComp!.address}
                    totalRent={leaseComp!.totalRent}
                    parcelSizeAcres={leaseComp!.parcelSizeAcres}
                    buildingSize={leaseComp!.buildingSize}
                    className={className}
                    onClick={onClick}
                    index={index}
                />
            )}
            {type === RecentItemType.SaleComp && (
                <SaleCompInfoWithIconAndNavigation
                    id={saleComp!.id}
                    address={saleComp!.address}
                    salePrice={saleComp!.salePrice}
                    pipedriveBuyerId={saleComp!.pipedriveBuyerId}
                    buildingSize={saleComp!.buildingSize}
                    parcelSizeAcres={saleComp!.parcelSizeAcres}
                    zoning={saleComp!.zoning}
                    className={className}
                    onClick={onClick}
                    index={index}
                />
            )}
            {type === RecentItemType.TenantInMarket && (
                <TenantInMarketInfoWithIconAndNavigation
                    id={tenantInMarket!.id}
                    description={tenantInMarket!.description}
                    landAreaMin={tenantInMarket!.landAreaMin}
                    landAreaMax={tenantInMarket!.landAreaMax}
                    buildingAreaMin={tenantInMarket!.buildingAreaMin}
                    buildingAreaMax={tenantInMarket!.buildingAreaMax}
                    className={className}
                    onClick={onClick}
                    index={index}
                />
            )}
            {type === RecentItemType.Company && (
                <CompanyInfoWithIconAndNavigation
                    id={company!.id}
                    name={company!.name}
                    className={className}
                    onClick={onClick}
                    index={index}
                />
            )}
        </>
    );
}

interface IRecentViewItemProps {
    id: number;
    type: RecentItemType;
    className?: string;
    index?: number;
}

export default observer(RecentViewItem);